import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { authService } from '../../services/api';
import axios from 'axios';
import AuthCard from './AuthCard';
import Invalidlink from './InvalidLink';

// A refusal (403: a switched-off workspace, an ended free trial, a free trial at its user limit, or a Deactivated account)
// shows its own words on a card of their own, as the agreed two lines: the first sentence, then the rest under it (K11 pt
// 2, S-14, S-6.1 pt 5). Removed 18 Oct 2026 (K11 pt 2): the "This link is no longer valid" card for every refusal, which
// hid the reason. A link that is invalid or expired (400) still shows that card.
const isRefusal = (error) => error && error.response && error.response.status === 403 && error.response.data
    && error.response.data.message;

const SelfSignupActivation = () => {
    const { token } = useParams();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: ''
    });
    const [status, setStatus] = useState(''); // 'form' | 'loading' | 'success' | 'error'
    const [errorMsg, setErrorMsg] = useState('');
    const [logoUrl, setLogoUrl] = useState(null);
    const [clientSlug, setClientSlug] = useState(null);
    // Set once the link is checked, so the logo is looked up in the link's workspace (S-4, agreed 18 Oct).
    const [linkChecked, setLinkChecked] = useState(false);

    /* ── Logo fetch ── */
    useEffect(() => {
        const CheckToken = async () => {
        try {
            const res = await authService.selfSignupValidate(token);
                if (res.data.slug) {
                    setClientSlug(res.data.slug);
                }
                if (res.data.success) {
                    if (res.data.isRegistered) {
                        // Existing external user: auto-login using the activate endpoint
                        const activateRes = await authService.selfSignupActivate(token, '', '');
                        if (activateRes.data.slug) {
                            setClientSlug(activateRes.data.slug);
                        }
                        if (activateRes.data.success) {
                            localStorage.setItem('isAuthenticated', 'true');
                            localStorage.setItem('userData', JSON.stringify(activateRes.data.user));
                            window.location.href = '/dashboard';
                            return;
                        }
                        setErrorMsg(activateRes.data.message || 'Failed to sign in');
                        setStatus('error');
                        return;
                    }

                    setStatus('form');
                } else {
                    setStatus('error');
                }
            } catch (err) {
                console.error("Failed to fetch logo", err);
                if (err.response?.data?.slug) {
                    setClientSlug(err.response.data.slug);
                }
                // A refusal gets its own card (K11 pt 2, S-14, S-6.1 pt 5).
                if (isRefusal(err)) {
                    setErrorMsg(err.response.data.message);
                    setStatus('refused');
                    return;
                }
                setStatus('error');
            } finally {
                setLinkChecked(true);
            }
        };
        CheckToken();
    }, [token]);

    /* ── Logo fetch ── */
    // After the link's check, which gives the page the link's workspace, so the logo is that workspace's in any browser
    // (S-4, agreed 18 Oct). Removed 18 Oct 2026 (S-4): looking it up before, which gave the browser's last workspace.
    useEffect(() => {
        if (!linkChecked) return;
        const fetchLogo = async () => {
        try {
            const res = await axios.get('/api/settings/logo');
                if (res.data.success && res.data.logoUrl) {
                    setLogoUrl(res.data.logoUrl);
                }                
                else
                {
                    setLogoUrl("/default-logo.png"); // Set to default logo if not provided
                }
            } catch (err) {
                console.error("Failed to fetch logo", err);
            }
        };
        fetchLogo();
    }, [linkChecked]);

    /* ── Form submit ── */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            setErrorMsg('Both first name and last name are required');
            return;
        }

        setStatus('loading');

        try {
            const response = await authService.selfSignupActivate(
                token,
                formData.firstName,
                formData.lastName
            );

            if (response.data.success) {
                setStatus('success');
                    // Redirect after 2 seconds
                    // Set localStorage like Login.jsx does
                    localStorage.setItem('isAuthenticated', 'true');
                    localStorage.setItem('userData', JSON.stringify(response.data.user));
                    setTimeout(() => {
                    window.location.href = '/dashboard';
                }, 3000);
            } else {
                if (response.data.slug) {
                    setClientSlug(response.data.slug);
                }
                setStatus('error');
                setErrorMsg(response.data.message || 'Failed to create account');
            }
        } catch (error) {
            if (error.response?.data?.slug) {
                setClientSlug(error.response.data.slug);
            }
            // A refusal gets its own card (K11 pt 2, S-14, S-6.1 pt 5).
            setStatus(isRefusal(error) ? 'refused' : 'error');
            setErrorMsg(error.response?.data?.message || 'Failed to create account');
        }
    };

    /* ────── Form view ────── */
    if (status === 'form') {
        return (
        <AuthCard logoUrl={logoUrl}>
            <div className="ac-body">
                <div className="ac-top">
                    <div className="ac-title">Almost there</div>
                    <div className="ac-sub">Tell us your name so your team can identify you.</div>
                </div>

                {errorMsg && (
                    <div className="ac-box ac-box-error">
                        <div className="ac-box-title">Error</div>
                        <div className="ac-box-sub">{errorMsg}</div>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="ac-field">
                        <label className="ac-field-label">First Name</label>
                        <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            className="ac-field-input"
                            placeholder="e.g. John"
                            required
                        />
                    </div>

                    <div className="ac-field">
                        <label className="ac-field-label">Last Name</label>
                        <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            className="ac-field-input"
                            placeholder="e.g. Doe"
                            required
                        />
                    </div>

                    <button type="submit" className="ac-btn-primary">
                        Get started
                    </button>
                </form>
            </div>
            <div className="ac-footer" />
        </AuthCard>
        );
    }

    /* ────── Loading view ────── */
    if (status === 'loading') {
        return (
        <AuthCard logoUrl={logoUrl}>
            <div className="ac-body" style={{ textAlign: 'center', padding: '40px 0' }}>
                <CircularProgress />
                <div className="ac-title" style={{ marginTop: '20px' }}>Creating your account...</div>
            </div>
            <div className="ac-footer" />
        </AuthCard>
        );
    }

    /* ────── Refusal view (K11 pt 2, S-14, S-6.1 pt 5) ────── */
    if (status === 'refused') {
        const cut = errorMsg.indexOf('. ');
        return (
            <AuthCard logoUrl={logoUrl}>
                <div className="ac-top">
                    <div className="ac-icon ac-icon-error">
                        <span style={{ fontSize: '24px' }}><CloseOutlinedIcon /></span>
                    </div>
                    <div className="ac-title">{cut > 0 ? errorMsg.slice(0, cut + 1) : errorMsg}</div>
                    {cut > 0 && <div className="ac-sub">{errorMsg.slice(cut + 2)}</div>}
                </div>
                <div className="ac-body">
                    {clientSlug && (
                        <div className="ac-field-link">
                            <Link to={`/learn/${clientSlug}`} className="ac-link">Go to sign in</Link>.
                        </div>
                    )}
                </div>
                <div className="ac-footer" />
            </AuthCard>
        );
    }

    /* ────── Error view ────── */
    if (status === 'error') {
        return (
            <Invalidlink logoUrl={logoUrl} slug={clientSlug} />
        );        
    }

    /* ────── Success view ────── */
    if (status === 'success') {
        return (
            <AuthCard logoUrl={logoUrl}>
                <div className="ac-body">
                    <div className="ac-icon ac-icon-success">
                        <span style={{ fontSize: '24px' }}><CheckOutlinedIcon/></span>
                    </div>
                    <div className="ac-top">
                        <div className="ac-title">You’re all set</div>
                        <div className="ac-sub">Taking you to your learning dashboard.</div>
                    </div>
                    <div className="ac-box ac-box-info">
                        <div className="ac-box-sub">If you came from an embed widget in another product, go back and click the <StarIcon/> icon again. It will recognize you now.</div>
                    </div>
                </div>
                <div className="ac-footer" />
            </AuthCard>
        );
    }
};

export default SelfSignupActivation;